<template>
  <MembershipPage
    title="You're Taking A Style Break">
    <template #aboveBox>
      <div class="d-flex flex-column gap-sm">
        <h4>Here's What To Expect</h4>
        <h6 v-if="!clientIsPaused">
          Your account will be paused from {{ pauseDates.startDate }} to {{ pauseDates.endDate }}
        </h6>
        <h6 v-else-if="pauseResumeDate">
          Your subscription will automatically resume on {{ formattedPauseResumeDate }}.
        </h6>
        <p>We'll remind you by email a few days before your subscription restarts. If you're ready to return sooner, you can restart your membership anytime.</p>
        <div
          v-if="clientIsPaused"
          class="d-flex flex-column gap-sm">
          <p v-if="canExtendPause">
            Need more time? You can extend your pause before {{ formattedPauseResumeDate }}.
          </p>
          <p v-else-if="isExtensionDateEligible && !eligibleForPause && ineligibleForPauseReason === 'ITEMS_OUT'">
            To extend your pause, please return or purchase any outstanding items first.
          </p>
          <p v-else-if="isExtensionDateEligible && !eligibleForPause">
            If you need to skip another month, please email us at <a href="mailto:hi@armoire.style?subject=I%20have%20a%20question">
              hi@armoire.style
            </a>
          </p>
          <p v-else-if="extensionEligibleDate">
            You can extend your pause starting on {{ formattedExtensionEligibleDate }}.
          </p>
        </div>
      </div>
    </template>
    <template #boxContent>
      <div class="d-flex flex-column gap-xl">
        <div
          v-if="clientIsPaused && hasOutstandingItems"
          class="d-flex flex-column gap-sm align-items-center">
          <p>Please return all items in your prepaid return bag by {{ clientIsPaused ? formattedPauseResumeDate : pauseDates.startDate }} to avoid being charged for outstanding items.</p>
          <BaseButton
            text="Print New Label"
            :to="printReturnLabelLink"/>
        </div>
        <div
          v-if="canExtendPause"
          class="d-flex flex-column gap-sm align-items-center">
          <BaseButton
            text="Extend Pause"
            :to="{ name: 'extend-pause' }"/>
        </div>
        <div class="d-flex flex-column gap-sm align-items-center">
          <b>Oops, didn't mean to pause?</b>
          <p>No worries, contact our support team to edit your membership.</p>
          <BaseButton
            text="Email Customer Support"
            :to="`mailto:hi@armoire.style?subject=${encodeURIComponent('Membership Inquiry')}`"/>
        </div>
      </div>
    </template>
    <template #belowBox>
      <BaseButton
        class="align-self-center"
        variant="text-link"
        text="Go to My Account"
        :to="{ name: 'account' }"/>
    </template>
  </MembershipPage>
</template>

<script setup>
import MembershipPage from '@/components/account/membership/new/MembershipPage.vue'
import { onMounted, computed } from 'vue'
import { useAccountStore } from '@/stores/account.js'
import { useClosetStore } from '@shared/stores/closet.js'
import { usePauseCancel } from '@/composables/pauseCancel.js'
import { useReturnCarrier } from '@/composables/returnCarrier.js'
import BaseButton from '@shared/components/ADORN/BaseButton.vue'
import useAnalytics from '@shared/composables/analytics.js'

const accountStore = useAccountStore()
const closetStore = useClosetStore()
const { pauseSubscription } = accountStore
const {
  pauseDates,
  getBilling,
  clientIsPaused,
  eligibleForPause,
  ineligibleForPauseReason,
  pauseResumeDate,
  formattedPauseResumeDate,
  extensionEligibleDate,
  formattedExtensionEligibleDate,
  isExtensionDateEligible,
  canExtendPause
} = usePauseCancel()
const { trackPageViewEvent } = useAnalytics()

const { printReturnLabelLink } = useReturnCarrier()

const hasOutstandingItems = computed(() => {
  return closetStore.outstandingItems.length > 0
})

onMounted(async () => {
  trackPageViewEvent('Pause Membership')
  await getBilling()
  if (eligibleForPause.value && !clientIsPaused.value) {
    await pauseSubscription({ months: 1 })
    await getBilling()
  }
})

</script>
